import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

/**
 * A daily lead row as returned by GET /daily-leads (workspace-filtered) and by
 * the `workspace-lead.generated` broadcast payload.
 */
export interface WorkspaceLeadRow {
  id: number;
  workspace_id: number | null;
  person_name: string | null;
  person_first_name: string | null;
  person_last_name: string | null;
  person_job_title: string | null;
  person_email?: string | null;
  person_linkedin_url: string | null;
  person_location: string | null;
  person_photo_url?: string | null;
  company_name: string | null;
  company_domain: string | null;
  /** Company logo from Apollo organization data (appended by the backend). */
  company_logo_url?: string | null;
  company_industry: string | null;
  company_employee_count: number | null;
  company_location?: string | null;
  icp_score: number | string | null;
  why_this_lead?: string | null;
  match_factors?: string[] | null;
  email_status?: string | null;
  status?: string | null;
  /** Raw Apollo person payload from the API list; carries photo_url. */
  apollo_person_data?: { photo_url?: string | null } | null;
  /** Score breakdown; carries the v4 below_threshold flag for backfilled leads. */
  icp_match_breakdown?: { below_threshold?: boolean; normal_threshold?: number } | null;
  /** Set directly on websocket rows (the broadcast doesn't ship the breakdown). */
  below_threshold?: boolean;
  /** Extra qualifying people at the same company, hidden until revealed (paid). */
  hidden_company_leads_count?: number;
  /** Set on same-company leads returned by the reveal endpoint. */
  parent_lead_id?: number | null;
  is_enhanced?: boolean;
  enhanced_report_id?: number | null;
  is_dismissed?: boolean;
  created_at?: string;
  /** Set on leads of an unsaved prospecting list ("Proceed to Leads"). */
  prospecting_list_id?: string | null;
  /** Prospecting-list leads expire 24 h after they're generated. */
  expires_at?: string | null;
}

/** Settings DTO from GET/PUT /icps/{id}/lead-settings (v3: ICP-scoped). */
export interface IcpLeadSettings {
  icp_id: number;
  icp_name: string | null;
  daily_leads_enabled: boolean;
  daily_leads_count: number;
  daily_leads_time: string | null;
  daily_leads_timezone: string | null;
  /** Saved business-category refinement applied by scheduled daily runs. */
  daily_leads_business_categories: string[];
  /** Saved company-product terms (Advanced dialog) applied by scheduled runs. */
  daily_leads_company_products: string[];
  credit_cost_manual: number;
  credit_cost_scheduled: number;
  /** Leads covered by the base run cost; extras add per_extra_lead each. */
  included_leads: number;
  per_extra_lead: number;
  /** Per-lead price for revealing hidden same-company leads. */
  company_reveal_cost_per_lead: number;
  remaining_manual_generations: number;
  max_manual_per_day: number;
  /** The whole pool was walked without a single new lead — nothing left to generate. */
  pool_completed: boolean;
  pool_completed_at: string | null;
  min_leads: number;
  max_leads: number;
}

export interface BusinessCategoryGroup {
  industry: string;
  categories: string[];
}

export interface BusinessCategoriesResult {
  icp_id: number;
  filtered_by_icp_industries: boolean;
  groups: BusinessCategoryGroup[];
}

export interface GenerateLeadsResult {
  run_id: number;
  icp_id?: number;
  workspace_id?: number;
  leads_requested: number;
  credit_cost: number;
  remaining_generations: number;
}

export interface IcpGenerationStatus {
  icp_id: number;
  has_active_generation: boolean;
  /** Saving the ICP turns its daily automation off — the edit form warns first. */
  automation_enabled: boolean;
  active_run: { id: number; status: string; run_type: string; leads_found: number } | null;
}

/** Laravel paginator envelope for lead lists. */
export interface PaginatedLeads {
  data: WorkspaceLeadRow[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/** A generation run row for the Activity tab (GET /daily-lead-runs). */
export interface LeadRunRow {
  id: number;
  run_type: 'manual' | 'scheduled' | 'onboarding';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  run_date: string;
  leads_found: number;
  companies_processed: number;
  credits_consumed: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface PaginatedRuns {
  data: LeadRunRow[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface LeadPoolEstimate {
  icp_id: number;
  available_companies: number | null;
  persons_estimate: number | null;
}

export interface WorkspaceLeadRun {
  id: number;
  workspace_id: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  leads_found: number;
  credits_consumed: number;
  error_message: string | null;
  daily_leads_count?: number;
}

/**
 * API client for the simplified, workspace-scoped Leads page. Reuses the
 * daily-leads endpoints (list / unlock / enhance / dismiss / runs) plus the
 * new workspace generate + lead-settings endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkspaceLeadsService {

  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  private options(token: string, params?: HttpParams) {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }),
      ...(params ? { params } : {}),
    };
  }

  /** GET /daily-leads?filter[icp_id]=X — newest first; active or dismissed; paginated. */
  getLeads(icpId: number, perPage = 10, dismissed = false, page = 1): Observable<{ data: PaginatedLeads }> {
    return this.authService.token().pipe(
      switchMap(token => {
        const params = new HttpParams()
          .set('filter[icp_id]', icpId.toString())
          .set('filter[is_dismissed]', dismissed ? '1' : '0')
          .set('sort', '-created_at')
          .set('per_page', perPage.toString())
          .set('page', page.toString());

        return this.http.get<{ data: PaginatedLeads }>(
          `${this.apiUrl}/daily-leads`,
          this.options(token, params),
        );
      })
    );
  }

  /** GET /daily-lead-runs?filter[icp_id]=X — generation activity, newest first. */
  getRuns(icpId: number, perPage = 10, page = 1): Observable<{ data: PaginatedRuns }> {
    return this.authService.token().pipe(
      switchMap(token => {
        const params = new HttpParams()
          .set('filter[icp_id]', icpId.toString())
          .set('sort', '-created_at')
          .set('per_page', perPage.toString())
          .set('page', page.toString());

        return this.http.get<{ data: PaginatedRuns }>(
          `${this.apiUrl}/daily-lead-runs`,
          this.options(token, params),
        );
      })
    );
  }

  /**
   * GET /daily-leads/export?icp_id=X — Excel-compatible CSV of the ICP's
   * leads (name, position, company, industry, unlocked email, LinkedIn,
   * ICP score, ...). Returned as a Blob for client-side download.
   */
  exportLeads(icpId: number, includeDismissed = false): Observable<Blob> {
    return this.authService.token().pipe(
      switchMap(token => {
        const params = new HttpParams()
          .set('icp_id', icpId.toString())
          .set('include_dismissed', includeDismissed ? '1' : '0');

        return this.http.get(`${this.apiUrl}/daily-leads/export`, {
          headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` }),
          params,
          responseType: 'blob',
        });
      })
    );
  }

  /** GET /icps/{id}/lead-pool-estimate — available companies / persons in pool. */
  getPoolEstimate(icpId: number): Observable<{ data: LeadPoolEstimate }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: LeadPoolEstimate }>(
        `${this.apiUrl}/icps/${icpId}/lead-pool-estimate`,
        this.options(token),
      ))
    );
  }

  /** POST /icps/{id}/generate-leads (v3: generation is ICP-scoped). */
  generateLeads(
    icpId: number,
    count: number,
    businessCategories: string[] = [],
    companyProducts: string[] = [],
  ): Observable<{ data: GenerateLeadsResult }> {
    // Both keys are always sent: the backend treats a present key as "the
    // dialog's current state" (empty = no filter), and only falls back to
    // the ICP's saved filters when a key is absent.
    const body = {
      count,
      business_categories: businessCategories,
      company_products: companyProducts,
    };

    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: GenerateLeadsResult }>(
        `${this.apiUrl}/icps/${icpId}/generate-leads`,
        body,
        this.options(token),
      ))
    );
  }

  /**
   * GET /icps/{id}/business-categories — Veridion business categories
   * grouped by industry, filtered to the ICP's industries when they map.
   */
  getBusinessCategories(icpId: number): Observable<{ data: BusinessCategoriesResult }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: BusinessCategoriesResult }>(
        `${this.apiUrl}/icps/${icpId}/business-categories`,
        this.options(token),
      ))
    );
  }

  /** GET /icps/{id}/lead-generation-status — is a run in flight for this ICP? */
  getIcpGenerationStatus(icpId: number): Observable<{ data: IcpGenerationStatus }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: IcpGenerationStatus }>(
        `${this.apiUrl}/icps/${icpId}/lead-generation-status`,
        this.options(token),
      ))
    );
  }

  /** GET /daily-lead-runs/{id} — generation progress polling. */
  getRun(runId: number): Observable<{ data: WorkspaceLeadRun }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: WorkspaceLeadRun }>(
        `${this.apiUrl}/daily-lead-runs/${runId}`,
        this.options(token),
      ))
    );
  }

  /** GET /icps/{id}/lead-settings */
  getLeadSettings(icpId: number): Observable<{ data: IcpLeadSettings }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: IcpLeadSettings }>(
        `${this.apiUrl}/icps/${icpId}/lead-settings`,
        this.options(token),
      ))
    );
  }

  /** PUT /icps/{id}/lead-settings */
  updateLeadSettings(
    icpId: number,
    payload: Partial<Pick<IcpLeadSettings, 'daily_leads_enabled' | 'daily_leads_count' | 'daily_leads_time' | 'daily_leads_timezone' | 'daily_leads_business_categories' | 'daily_leads_company_products'>>,
  ): Observable<{ data: IcpLeadSettings }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.put<{ data: IcpLeadSettings }>(
        `${this.apiUrl}/icps/${icpId}/lead-settings`,
        payload,
        this.options(token),
      ))
    );
  }

  /** POST /daily-leads/{id}/unlock-email */
  unlockEmail(leadId: number): Observable<{ data: { id: number; person_email: string; email_status: string; credits_deducted: number } }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: { id: number; person_email: string; email_status: string; credits_deducted: number } }>(
        `${this.apiUrl}/daily-leads/${leadId}/unlock-email`,
        {},
        this.options(token),
      ))
    );
  }

  /**
   * POST /daily-leads/{id}/enhance — generates the full report into the
   * workspace picked in the dialog (v3: workspace chosen at report time).
   */
  enhanceLead(leadId: number, workspaceId?: number, regenerate = false): Observable<{ data: { lead: WorkspaceLeadRow; search_id: number } }> {
    const body: { workspace_id?: number; regenerate?: boolean } = {};
    if (workspaceId) {
      body.workspace_id = workspaceId;
    }
    if (regenerate) {
      body.regenerate = true;
    }

    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: { lead: WorkspaceLeadRow; search_id: number } }>(
        `${this.apiUrl}/daily-leads/${leadId}/enhance`,
        body,
        this.options(token),
      ))
    );
  }

  /** POST /daily-leads/{id}/dismiss */
  dismissLead(leadId: number): Observable<{ data: WorkspaceLeadRow }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: WorkspaceLeadRow }>(
        `${this.apiUrl}/daily-leads/${leadId}/dismiss`,
        {},
        this.options(token),
      ))
    );
  }

  /** POST /daily-leads/{id}/restore — bring a dismissed lead back. */
  /** DELETE /daily-leads/{id} — permanently delete a dismissed lead. */
  deleteLead(leadId: number): Observable<{ data: { id: number } }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.delete<{ data: { id: number } }>(
        `${this.apiUrl}/daily-leads/${leadId}`,
        this.options(token),
      ))
    );
  }

  restoreLead(leadId: number): Observable<{ data: WorkspaceLeadRow }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: WorkspaceLeadRow }>(
        `${this.apiUrl}/daily-leads/${leadId}/restore`,
        {},
        this.options(token),
      ))
    );
  }

  /**
   * POST /daily-leads/{id}/reveal-company-leads — paid reveal of hidden
   * same-company leads. `count` picks how many (highest-scoring first); the
   * charge is per revealed lead, and partial reveals leave the rest hidden.
   */
  revealCompanyLeads(leadId: number, count: number): Observable<{ data: { lead_id: number; credits_deducted: number; per_lead_cost: number; revealed_leads: WorkspaceLeadRow[]; remaining_hidden: number } }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: { lead_id: number; credits_deducted: number; per_lead_cost: number; revealed_leads: WorkspaceLeadRow[]; remaining_hidden: number } }>(
        `${this.apiUrl}/daily-leads/${leadId}/reveal-company-leads`,
        { count },
        this.options(token),
      ))
    );
  }

  /** PATCH /daily-leads/{id}/status */
  updateStatus(leadId: number, status: 'new' | 'viewed' | 'contacted' | 'converted' | 'dismissed'): Observable<{ data: WorkspaceLeadRow }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.patch<{ data: WorkspaceLeadRow }>(
        `${this.apiUrl}/daily-leads/${leadId}/status`,
        { status },
        this.options(token),
      ))
    );
  }
}
